/**
 * @file index.tsx
 *
 * @fileoverview Component that maps directly to CSS grid properties using styled-system.
 */

import { ReactNode } from "react";
import {
  AlignItemsProps,
  JustifyContentProps,
  JustifyItemsProps,
  JustifySelfProps,
} from "../../../../../theme/layout/itemsLayout";
import {
  excludeSpacingProps,
  generateMarginClasses,
  generatePaddingClasses,
  SpaceProps,
} from "../../../../../theme/spacing";
import { getScopedClasses } from "../../../../../utils/responsive";
import styles from "./cell.module.scss";

type TextAlignProps =
  | "center"
  | "end"
  | "justify"
  | "left"
  | "match-parent"
  | "right"
  | "start";

type DisplayProps =
  | "-moz-initial"
  | "inherit"
  | "initial"
  | "revert"
  | "unset"
  | "block"
  | "inline"
  | "run-in"
  | "-ms-flexbox"
  | "-ms-grid"
  | "-webkit-flex"
  | "flex"
  | "flow"
  | "flow-root"
  | "grid"
  | "ruby"
  | "table"
  | "contents"
  | "list-item"
  | "none";

type GridRow =
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | "auto"
  | "span 1"
  | "span 2"
  | "span 3"
  | "span 4"
  | "span 5"
  | "span 6"
  | "span 7"
  | "span 8"
  | "span 9"
  | "span 10"
  | "span 11"
  | "span 12";

type GridColumn =
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | "auto"
  | "span 1"
  | "span 2"
  | "span 3"
  | "span 4"
  | "span 5"
  | "span 6"
  | "span 7"
  | "span 8"
  | "span 9"
  | "span 10"
  | "span 11"
  | "span 12";

export type CellProps = SpaceProps & {
  /**
   * The contents to display inside.
   */
  children?: ReactNode;
  /**
   * Responsive grid colum prop.
   */
  gridRow?: GridRow | GridRow[];
  /**
   * Responsive grid row gap property.
   */
  gridRowGap?: number | number[];
  /**
   * Responsive grid colum prop.
   */
  gridColumn?: GridColumn | GridColumn[];
  /**
   * Responsive grid column gap property.
   */
  gridColumnGap?: number | number[];
  /**
   * Responsive width property.
   */
  width?: number | number[];
  /**
   * Responsive height property.
   */
  height?: number | number[];
  /**
   * Responsive align items prop.
   */
  alignItems?: AlignItemsProps | AlignItemsProps[];
  /**
   * Responsive justify items prop.
   */
  justifyItems?: JustifyItemsProps | JustifyItemsProps[];
  /**
   * Responsive justify content prop.
   */
  justifyContent?: JustifyContentProps | JustifyContentProps[];
  /**
   * Justify self responsive props.
   */
  justifySelf?: JustifySelfProps | JustifySelfProps[];
  /**
   * Responsive text align props.
   */
  textAlign?: TextAlignProps | TextAlignProps[];
  /**
   * Responsive display props.
   */
  display?: DisplayProps | DisplayProps[];
  /**
   * Grid are prop.
   */
  gridArea?: string;
  /**
   * The className.
   * This prop gets appended to the already existing classes.
   */
  className?: string;
};

/**
 * A Cell is a child of the Grid Component. It allows items to be specifically displayed in one or multiple
 * cells of a grid. It maps directly to the CSS Grid spec.
 */
export const Cell = ({
  children,
  gridRow,
  gridRowGap,
  gridColumn,
  gridColumnGap,
  width,
  height,
  alignItems,
  justifyItems,
  justifyContent,
  justifySelf,
  textAlign,
  display,
  gridArea,
  className = "",
  ...props
}: CellProps) => {
  return (
    <div
      className={`
        ${generateMarginClasses(props)}
        ${generatePaddingClasses(props)}
        ${getScopedClasses(
          // When we pass gridRow={"span 2"} this needs to be converted to a class like "span-2"
          // It can also be an array e.g. gridRow={["span 2", "span 1"]}
          // And lastly it can also have number values e.g. gridRow={[1, 2]} or combine both {[1, "span 2"]}
          styles,
          "cell-row",
          Array.isArray(gridRow)
            ? gridRow.map((i) => i.toString().replace("span ", "span-"))
            : gridRow?.toString().replace("span ", "span-")
        )}
        ${getScopedClasses(styles, "cell-rowgap", gridRowGap)}
        ${getScopedClasses(
          // When we pass gridColumn={"span 2"} this needs to be converted to a class like "span-2"
          // It can also be an array e.g. gridColumn={["span 2", "span 1"]}
          // And lastly it can also have number values e.g. gridColumn={[1, 2]} or combine both {[1, "span 2"]}
          styles,
          "cell-column",
          Array.isArray(gridColumn)
            ? gridColumn.map((i) => i.toString().replace("span ", "span-"))
            : gridColumn?.toString().replace("span ", "span-")
        )}
        ${getScopedClasses(styles, "cell-columngap", gridColumnGap)}
        ${getScopedClasses(styles, "cell-width", width)}
        ${getScopedClasses(styles, "cell-height", height)}
        ${getScopedClasses(styles, "ai", alignItems)}
        ${getScopedClasses(styles, "ji", justifyItems)}
        ${getScopedClasses(styles, "jc", justifyContent)}
        ${getScopedClasses(styles, "js", justifySelf)}
        ${getScopedClasses(styles, "cell-ta", textAlign)}
        ${getScopedClasses(styles, "cell-dsp", display)}
        ${styles.cell}
        ${className}
      `.trim()}
      style={{
        ...(gridArea && { gridArea }),
      }}
      {...excludeSpacingProps(props)}
    >
      {children}
    </div>
  );
};
export default Cell;
